/**
 * Message Content Renderer with Anchor Support
 * 
 * Renders message content, parsing anchors and replacing them with interactive components.
 */

import React from "react";
import { MarkdownRenderer } from "../../features/kquant/components/MarkdownRenderer";
import { parseAnchors, hasAnchors } from "../../features/kquant/utils/anchorParser";
import { AnchorComponent } from "../../features/kquant/components/AnchorComponents";

type MessageContentProps = {
  content: string;
  theme?: "light" | "dark";
};

/**
 * Renders message content with anchor parsing support.
 * If anchors are detected, splits content into text and component segments.
 * Text segments are rendered with MarkdownRenderer, anchors become interactive components.
 */
export function MessageContent({ content, theme = "light" }: MessageContentProps) {
  // Quick check: if no anchors, render as plain markdown
  if (!hasAnchors(content)) {
    return <MarkdownRenderer content={content} theme={theme} />;
  }

  // Parse content into segments
  const segments = parseAnchors(content);

  return (
    <>
      {segments.map((segment, idx) => {
        if (segment.type === 'text') {
          // Render text segments as markdown
          return (
            <MarkdownRenderer 
              key={idx} 
              content={segment.content} 
              theme={theme} 
            />
          );
        } else {
          // Render component anchors
          return (
            <AnchorComponent
              key={idx}
              componentName={segment.componentName!}
              params={segment.params!}
              theme={theme}
            />
          );
        }
      })}
    </>
  );
}
